import { useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import type { Conversation, Message } from '@/lib/messages';

interface ChatWindowProps {
  conversation: Conversation;
  messages: Message[];
  currentUserId: string;
  loading: boolean;
  onSend: (content: string) => Promise<void>;
  onBack: () => void;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function formatMessageTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatMessageDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function ChatWindow({
  conversation,
  messages,
  currentUserId,
  loading,
  onSend,
  onBack,
}: ChatWindowProps) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const name = conversation.other_user?.full_name || conversation.other_user?.email || 'Utilisateur';

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await onSend(content);
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  const grouped: { date: string; items: Message[] }[] = [];
  for (const m of messages) {
    const date = new Date(m.created_at).toLocaleDateString('fr-FR');
    const last = grouped[grouped.length - 1];
    if (last && last.date === date) {
      last.items.push(m);
    } else {
      grouped.push({ date, items: [m] });
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-background-200/70">
        <button
          onClick={onBack}
          className="lg:hidden w-9 h-9 flex items-center justify-center text-foreground-50"
          aria-label="Retour"
        >
          <i className="ri-arrow-left-line text-xl" />
        </button>
        {conversation.other_user?.avatar_url ? (
          <img
            src={conversation.other_user.avatar_url}
            alt={name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-400 font-semibold">
            {getInitials(conversation.other_user?.full_name)}
          </div>
        )}
        <div className="min-w-0">
          <span className="block text-sm font-semibold text-foreground-50 truncate">{name}</span>
          {conversation.other_user?.role && (
            <span className="block text-xs text-foreground-500 truncate">
              {conversation.other_user.role}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-loader-4-line text-primary-400 animate-spin" />
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
              <i className="ri-chat-smile-2-line text-xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-400">Envoyez votre premier message à {name}</p>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.date} className="space-y-2">
              <div className="flex justify-center">
                <span className="text-xs text-foreground-500 bg-background-100 px-3 py-1 rounded-full">
                  {formatMessageDate(group.items[0].created_at)}
                </span>
              </div>
              {group.items.map((m) => {
                const mine = m.sender_id === currentUserId;
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                        mine
                          ? 'bg-primary-500 text-foreground-950 rounded-br-md'
                          : 'bg-background-100 text-foreground-50 rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.content}</p>
                      <span
                        className={`block text-[10px] mt-1 ${
                          mine ? 'text-foreground-950/70' : 'text-foreground-500'
                        }`}
                      >
                        {formatMessageTime(m.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-background-200/70">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Écrivez votre message..."
            rows={1}
            className="flex-1 resize-none bg-background-50 border border-background-300/60 rounded-lg px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-950 hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Envoyer"
          >
            <i className="ri-send-plane-2-fill text-lg" />
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChatWindow;